/**
 * Match clock + break timer maths for the OBS overlay page. The server sends the time
 * left as of `serverNowMs`; the page counts from the moment it received that state, so
 * the overlay never depends on the server and browser clocks agreeing.
 */

export interface MatchClockFields {
    matchDurationMinutes: number | null
    matchRemainingMs: number | null
    matchRunning: boolean
    breakStartedAtMs: number | null
    serverNowMs: number
    /** Local Date.now() when this state arrived. */
    receivedAtMs: number
}

/** Match time left now; null when there is no match clock or it has not started yet. */
export function matchRemainingAt(s: MatchClockFields, nowMs: number): number | null {
    if (!s.matchDurationMinutes || s.matchRemainingMs == null) return null
    if (!s.matchRunning) return s.matchRemainingMs
    return Math.max(0, s.matchRemainingMs - Math.max(0, nowMs - s.receivedAtMs))
}

/** Time the break has lasted so far (counts up); 0 when not on a break. */
export function breakElapsedAt(s: MatchClockFields, nowMs: number): number {
    if (s.breakStartedAtMs == null) return 0
    const atReceipt = s.serverNowMs - s.breakStartedAtMs
    return Math.max(0, atReceipt + Math.max(0, nowMs - s.receivedAtMs))
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** "MM:SS" with minutes not wrapped at 60 (a 120 minute match starts at "120:00"); rounds up so 00:00 only shows at the end. */
export function formatCountdown(ms: number): string {
    const total = Math.ceil(Math.max(0, ms) / 1000)
    return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`
}

/** "MM:SS" for a count-up timer (rounds down). */
export function formatElapsed(ms: number): string {
    const total = Math.floor(Math.max(0, ms) / 1000)
    return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`
}
